import React, {
  createContext,
  useMemo,
  useState,
  useCallback,
  useContext,
  useEffect,
} from "react";
import { ethers } from "ethers";
import { toast } from "react-hot-toast";
import config from "../configs";
import trinityManagerArtifact from "../configs/abis/trinity-manager.json";
import trinitySharesArtifact from "../configs/abis/trinity-shares.json";
import { useEthersSigner } from "../hooks/useEthersSigner";
import { useEthersProvider } from "../hooks/useEthersProvider";
import { useAccount } from "wagmi";
import { AppContext } from "./context";
import { request } from "../admin/plugins/axios";
import {
  getPurchaseHashes,
  getCommissionHashes,
} from "../pages/presale/presaleContractFunctions";

const { abi: trinityManagerAbi } = trinityManagerArtifact;
const { abi: trinitySharesAbi } = trinitySharesArtifact;

const ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";
const SHARE_PACKAGES = [5, 15, 50, 100, 250];
const TRINITY_TOKEN_ADDRESS = config.get(
  "smartContract.TRINITY_SHARES_ADDRESS"
);
const DEFAULT_TOKEN_SYMBOL = "TRI";
const DEFAULT_TOKEN_DECIMALS = 0;
const DEFAULT_TOKEN_NAME = "Trinity Shares";
const TRINITY_TOKEN_IMAGE =
  typeof window !== "undefined" ? `${window.location.origin}/logo192.png` : "";
const REFRESH_INTERVAL = 30000; // 30 seconds

const PresaleContext = createContext(null);

const parseErrorMessage = (error) => {
  if (!error) return "Transaction failed";
  if (error.reason) return error.reason;
  if (error.data?.message) return error.data.message;
  if (error.error?.message) return error.error.message;
  return error.message || "Transaction failed";
};

export const PresaleProvider = ({ children }) => {
  const contractAddress = config.get("smartContract.TRINITY_MANAGER_ADDRESS");
  const signer = useEthersSigner();
  const provider = useEthersProvider();
  const { address } = useAccount();
  const { userDataSponsor, userSponsorAddress } = useContext(AppContext) || {};

  const [balance, setBalance] = useState("0");
  const [selectedShares, setSelectedShares] = useState(SHARE_PACKAGES[0]);
  const [sharePriceWei, setSharePriceWei] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [isReferrerVerified, setIsReferrerVerified] = useState(false);
  const [checkingReferrer, setCheckingReferrer] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [tick, setTick] = useState(0);
  const [tokenMetadata, setTokenMetadata] = useState({
    symbol: DEFAULT_TOKEN_SYMBOL,
    decimals: DEFAULT_TOKEN_DECIMALS,
    name: DEFAULT_TOKEN_NAME,
    balance: null,
  });

  const contract = useMemo(() => {
    if (!signer || !contractAddress) return null;
    return new ethers.Contract(contractAddress, trinityManagerAbi, signer);
  }, [contractAddress, signer]);

  const readOnlyContract = useMemo(() => {
    if (!provider || !contractAddress) return null;
    return new ethers.Contract(contractAddress, trinityManagerAbi, provider);
  }, [contractAddress, provider]);

  const tokenReadProvider = provider || signer?.provider || null;

  const sharesContract = useMemo(() => {
    if (!TRINITY_TOKEN_ADDRESS || !tokenReadProvider) return null;
    try {
      return new ethers.Contract(
        TRINITY_TOKEN_ADDRESS,
        trinitySharesAbi,
        tokenReadProvider
      );
    } catch (err) {
      console.error("Failed to instantiate shares contract", err);
      return null;
    }
  }, [tokenReadProvider]);

  const refresh = useCallback(() => setTick((prev) => prev + 1), []);

  const fetchBalance = useCallback(async () => {
    if (!provider || !address) return;
    try {
      const currentBalance = await provider.getBalance(address);
      setBalance(ethers.utils.formatEther(currentBalance));
    } catch (e) {
      console.error("Failed to fetch balance", e);
    }
  }, [provider, address]);

  const fetchUserInfo = useCallback(async () => {
    if (!contract && !readOnlyContract) return;
    const caller = contract || readOnlyContract;
    try {
      const info = await caller.getMyInfo();
      setUserInfo({
        userAddress: info.userAddress,
        referrer: info.referrer,
        totalShares: Number(info.totalShares || 0),
        directs: info.directs || [],
      });
    } catch (e) {
      console.error("Failed to fetch user info", e);
    }
  }, [contract, readOnlyContract]);

  const fetchSharePrice = useCallback(
    async (shares = selectedShares) => {
      if (!readOnlyContract || !shares) {
        setSharePriceWei(null);
        return;
      }
      try {
        const price = await readOnlyContract.getPriceForShares(
          ethers.BigNumber.from(String(shares))
        );
        setSharePriceWei(price);
      } catch (e) {
        console.error("Failed to fetch share price", e);
        setSharePriceWei(null);
      }
    },
    [readOnlyContract, selectedShares]
  );

  const verifyReferrer = useCallback(
    async (refAddress) => {
      if (!readOnlyContract || !refAddress || refAddress === ZERO_ADDRESS) {
        setIsReferrerVerified(false);
        return false;
      }
      try {
        setCheckingReferrer(true);
        const result = await readOnlyContract.checkReferrerIsVerified(
          refAddress
        );
        setIsReferrerVerified(Boolean(result));
        return Boolean(result);
      } catch (e) {
        console.error("Failed to verify referrer", e);
        setIsReferrerVerified(false);
        return false;
      } finally {
        setCheckingReferrer(false);
      }
    },
    [readOnlyContract]
  );

  const determineReferrer = useCallback(
    async (overrideReferrer) => {
      if (overrideReferrer && (await verifyReferrer(overrideReferrer))) {
        return overrideReferrer;
      }

      if (userInfo?.referrer && userInfo.referrer !== ZERO_ADDRESS) {
        return userInfo.referrer;
      }

      if (userDataSponsor?.wallet_address) {
        const sponsorAddress = userDataSponsor.wallet_address;
        if (await verifyReferrer(sponsorAddress)) {
          return sponsorAddress;
        }
      }

      if (userSponsorAddress) {
        if (await verifyReferrer(userSponsorAddress)) {
          return userSponsorAddress;
        }
      }

      return ZERO_ADDRESS;
    },
    [userDataSponsor, userSponsorAddress, userInfo, verifyReferrer]
  );

  const loadTokenMetadataFromChain = useCallback(async () => {
    if (!sharesContract) {
      return null;
    }
    try {
      const [symbolRes, decimalsRes, nameRes, balanceRes] = await Promise.all([
        sharesContract.symbol().catch(() => null),
        sharesContract.decimals().catch(() => null),
        sharesContract.name().catch(() => null),
        address
          ? sharesContract.balanceOf(address).catch(() => null)
          : Promise.resolve(null),
      ]);

      const decimalsNumber =
        decimalsRes !== null && decimalsRes !== undefined
          ? Number(decimalsRes)
          : DEFAULT_TOKEN_DECIMALS;

      const normalized = {
        symbol: symbolRes || DEFAULT_TOKEN_SYMBOL,
        decimals: Number.isFinite(decimalsNumber)
          ? decimalsNumber
          : DEFAULT_TOKEN_DECIMALS,
        name: nameRes || DEFAULT_TOKEN_NAME,
        balance:
          balanceRes && decimalsNumber >= 0
            ? ethers.utils.formatUnits(balanceRes, decimalsNumber)
            : null,
      };

      return normalized;
    } catch (err) {
      console.error("Failed to load token metadata", err);
      return null;
    }
  }, [sharesContract, address]);

  useEffect(() => {
    let cancelled = false;
    const fetchMetadata = async () => {
      const metadata = await loadTokenMetadataFromChain();
      if (metadata && !cancelled) {
        setTokenMetadata(metadata);
      }
    };

    if (sharesContract) {
      fetchMetadata();
    } else {
      setTokenMetadata((prev) => ({
        symbol: prev?.symbol || DEFAULT_TOKEN_SYMBOL,
        decimals: prev?.decimals ?? DEFAULT_TOKEN_DECIMALS,
        name: prev?.name || DEFAULT_TOKEN_NAME,
        balance: prev?.balance || null,
      }));
    }

    return () => {
      cancelled = true;
    };
  }, [sharesContract, loadTokenMetadataFromChain]);

  const ensureTokenMetadata = useCallback(async () => {
    if (tokenMetadata?.symbol && tokenMetadata?.decimals !== undefined) {
      return tokenMetadata;
    }
    const metadata = await loadTokenMetadataFromChain();
    if (metadata) {
      setTokenMetadata(metadata);
      return metadata;
    }
    return {
      symbol: DEFAULT_TOKEN_SYMBOL,
      decimals: DEFAULT_TOKEN_DECIMALS,
      name: DEFAULT_TOKEN_NAME,
      balance: null,
    };
  }, [tokenMetadata, loadTokenMetadataFromChain]);

  const addTokenToWallet = useCallback(async () => {
    if (typeof window === "undefined") return false;
    if (!window.ethereum?.request) return false;
    if (!TRINITY_TOKEN_ADDRESS) return false;
    if (localStorage.getItem("trinityTokenAdded") === "true") {
      return true;
    }

    const metadata = await ensureTokenMetadata();
    const symbol = metadata?.symbol || DEFAULT_TOKEN_SYMBOL;
    const decimals =
      typeof metadata?.decimals === "number"
        ? metadata.decimals
        : DEFAULT_TOKEN_DECIMALS;

    try {
      const wasAdded = await window.ethereum.request({
        method: "wallet_watchAsset",
        params: {
          type: "ERC20",
          options: {
            address: TRINITY_TOKEN_ADDRESS,
            symbol,
            decimals,
            image: TRINITY_TOKEN_IMAGE || undefined,
          },
        },
      });

      if (wasAdded) {
        localStorage.setItem("trinityTokenAdded", "true");
        toast.success("Trinity token added to your wallet");
      }

      return wasAdded;
    } catch (err) {
      console.error("Failed to add token to wallet", err);
      return false;
    }
  }, [ensureTokenMetadata]);

  const purchase = useCallback(
    async ({ shares, referrerOverride } = {}) => {
      if (!contract || !signer) {
        setError("Wallet is not connected");
        toast.error("Wallet is not connected");
        return;
      }

      const targetShares = shares || selectedShares;
      if (!targetShares) {
        setError("Please select a package");
        toast.error("Please select a package");
        return;
      }

      try {
        setLoading(true);
        setError("");

        console.log(
          "purchase:getPriceForShares",
          targetShares,
          readOnlyContract?.address
        );
        const price = await readOnlyContract.getPriceForShares(
          ethers.BigNumber.from(String(targetShares))
        );
        const referrer = await determineReferrer(referrerOverride);

        const tx = await contract.buyShares(
          ethers.BigNumber.from(String(targetShares)),
          referrer,
          {
            value: price,
          }
        );

        const transactionHash = tx.hash;
        console.log("Transaction sent:", { transactionHash });

        // Wait for transaction to be mined
        const receipt = await tx.wait();
        console.log("Transaction confirmed:", { receipt });

        // Extract purchaseId and commissionIds from events
        let purchaseId = null;
        const commissionIds = [];

        // Find Purchased event
        const purchasedEvent = receipt.events?.find(
          (e) => e.event === "Purchased"
        );
        if (purchasedEvent) {
          purchaseId = purchasedEvent.args?.id?.toString();
          console.log("Purchased event:", {
            id: purchaseId,
            buyer: purchasedEvent.args?.buyer,
            shares: purchasedEvent.args?.shares?.toString(),
          });
        }

        // Find all Commission events
        const commissionEvents = receipt.events?.filter(
          (e) => e.event === "Commission"
        );
        if (commissionEvents && commissionEvents.length > 0) {
          commissionEvents.forEach((event) => {
            const commissionId = event.args?.id?.toString();
            if (commissionId) {
              commissionIds.push(Number(commissionId));
            }
          });
          console.log("Commission events:", {
            count: commissionEvents.length,
            ids: commissionIds,
          });
        }

        // Print full contract response
        console.log("response of purchase contract process", {
          transaction: {
            hash: transactionHash,
            from: tx.from,
            to: tx.to,
            value: tx.value?.toString(),
            gasLimit: tx.gasLimit?.toString(),
            gasPrice: tx.gasPrice?.toString(),
          },
          receipt: {
            transactionHash: receipt.transactionHash,
            blockNumber: receipt.blockNumber,
            blockHash: receipt.blockHash,
            from: receipt.from,
            to: receipt.to,
            gasUsed: receipt.gasUsed?.toString(),
            effectiveGasPrice: receipt.effectiveGasPrice?.toString(),
            status: receipt.status,
            logs: receipt.logs,
            events: receipt.events,
          },
          events: {
            purchased: purchasedEvent
              ? {
                  id: purchasedEvent.args?.id?.toString(),
                  buyer: purchasedEvent.args?.buyer,
                  shares: purchasedEvent.args?.shares?.toString(),
                  price: purchasedEvent.args?.price?.toString(),
                  timestamp: purchasedEvent.args?.timestamp?.toString(),
                }
              : null,
            commissions:
              commissionEvents?.map((event) => ({
                id: event.args?.id?.toString(),
                recipient: event.args?.recipient,
                amount: event.args?.amount?.toString(),
                level: event.args?.level?.toString(),
              })) || [],
          },
        });

        // Prepare API payload
        const apiPayload = {
          transaction_id: purchaseId ? Number(purchaseId) : null,
          transaction_hash: transactionHash,
          commision_transaction_ids: commissionIds,
        };

        console.log("Sending to API:", apiPayload);
        console.log("API URL:", config.get("smartContract.API_URL"));

        // Send to backend API
        try {
          const apiUrl = config.get("smartContract.API_URL");
          const endpoint = `${apiUrl}/transaction/purchase`;
          console.log("📤 Calling API:", endpoint);
          console.log("📦 Payload:", apiPayload);

          const response = await request("post", endpoint, apiPayload);

          console.log("✅ API Response Status:", response.status);
          console.log("✅ API Response Data:", response.data);
          toast.success("Purchase completed and synced with backend");

          // Get hashes from backend after successful purchase sync
          if (purchaseId) {
            try {
              console.log("📤 Getting purchase hashes for ID:", purchaseId);
              const purchaseHashesResponse = await getPurchaseHashes([
                Number(purchaseId),
              ]);
              console.log(
                "✅ Purchase hashes from backend:",
                purchaseHashesResponse
              );
            } catch (hashError) {
              console.error(
                "❌ Failed to get purchase hashes from backend:",
                hashError
              );
              console.error("Error details:", {
                message: hashError?.message,
                response: hashError?.response?.data,
                status: hashError?.response?.status,
                url: hashError?.config?.url,
              });
            }
          }

          // Get commission hashes from backend if there are commission IDs
          if (commissionIds.length > 0) {
            try {
              console.log(
                "📤 Getting commission hashes for IDs:",
                commissionIds
              );
              const commissionHashesResponse = await getCommissionHashes(
                commissionIds
              );
              console.log(
                "✅ Commission hashes from backend:",
                commissionHashesResponse
              );
            } catch (hashError) {
              console.error(
                "❌ Failed to get commission hashes from backend:",
                hashError
              );
              console.error("Error details:", {
                message: hashError?.message,
                response: hashError?.response?.data,
                status: hashError?.response?.status,
                url: hashError?.config?.url,
              });
            }
          }
        } catch (apiError) {
          console.error("❌ Failed to send purchase data to API:", apiError);
          console.error("Error details:", {
            message: apiError?.message,
            response: apiError?.response?.data,
            status: apiError?.response?.status,
            statusText: apiError?.response?.statusText,
            url: apiError?.config?.url,
            method: apiError?.config?.method,
            baseURL: apiError?.config?.baseURL,
          });

          // Show detailed error message
          const errorMessage = apiError?.response?.status
            ? `API Error ${apiError.response.status}: ${
                apiError.response.statusText || "Not Found"
              }`
            : apiError?.message || "Unknown error";

          console.error("Full error object:", apiError);
          // Don't fail the purchase if API call fails
          // toast.error(
          //   `Purchase completed but failed to sync with backend: ${errorMessage}`
          // );
        }

        console.log("buyShares success", {
          shares: targetShares,
          referrer,
          price: price.toString(),
          transactionHash,
          purchaseId,
          commissionIds,
        });
        setSuccess("Purchase completed successfully");
        toast.success("Purchase completed successfully");
        await addTokenToWallet();
        refresh();
      } catch (e) {
        const message = parseErrorMessage(e);
        console.error("buyShares failed", e);
        setError(message);
        toast.error(message);
      } finally {
        setLoading(false);
      }
    },
    [
      addTokenToWallet,
      contract,
      determineReferrer,
      readOnlyContract,
      refresh,
      selectedShares,
      signer,
    ]
  );

  useEffect(() => {
    fetchBalance();
  }, [fetchBalance, tick]);

  useEffect(() => {
    fetchUserInfo();
  }, [fetchUserInfo, tick]);

  useEffect(() => {
    fetchSharePrice();
  }, [fetchSharePrice]);

  useEffect(() => {
    if (!address) return;
    const interval = setInterval(refresh, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [address, refresh]);

  useEffect(() => {
    if (!userDataSponsor?.wallet_address) return;
    verifyReferrer(userDataSponsor.wallet_address);
  }, [userDataSponsor, verifyReferrer]);

  const contextValue = useMemo(
    () => ({
      address,
      balance,
      telv: selectedShares,
      setTelv: setSelectedShares,
      availablePackages: SHARE_PACKAGES,
      sharePriceWei,
      sharePriceBnb: sharePriceWei
        ? Number(ethers.utils.formatEther(sharePriceWei))
        : 0,
      userInfo,
      isReferrerVerified,
      checkingReferrer,
      verifyReferrer,
      purchase,
      loading,
      error,
      setError,
      success,
      setSuccess,
      refetch: refresh,
      determineReferrer,
      refreshCounter: tick,
    }),
    [
      address,
      balance,
      selectedShares,
      sharePriceWei,
      userInfo,
      isReferrerVerified,
      checkingReferrer,
      verifyReferrer,
      purchase,
      loading,
      error,
      success,
      refresh,
      determineReferrer,
      tick,
    ]
  );

  return (
    <PresaleContext.Provider value={contextValue}>
      {children}
    </PresaleContext.Provider>
  );
};

export const usePresale = () => {
  return useContext(PresaleContext);
};
